/**
 * @Description 前端场景类
 * @Author iCEA
 * @Date 2020-10-29
 * @Version 1.0
 */

import { Camera } from './Camera'
import { CameraController } from './CameraController'
import { Renderer } from './Renderer'
import { Listener } from './Listener'
import * as Utils from './Utils'
import { config } from './config'

const SwitchTypeEnum = {
  Pano: 0,
  In3D: 1,
  Fade: 2
}

export class View {
  /**
   * 一个场景点的构造函数
   * @param {number} id 场景点位列表id
   * @param {number} index 场景点位对应的相机点位id
   * @param {number} lon 场景点位的相机经度
   * @param {number} lat 场景点位的相机纬度
   */
  constructor (id, index, lon, lat) {
    /**
     * @type {number} 场景点位的id
     */
    this.id = id
    /**
     * @type {number} 场景点位的相机点位id
     */
    this.pointIndex = index
    /**
     * @type {number} 场景点位的经度
     */
    this.lon = lon
    /**
     * @type {number} 场景点位的纬度
     */
    this.lat = lat
  }

  get isSingleView () {
    return Utils.isSingleView(this.pointIndex)
  }
}

class ViewSet {
  /**
   * 场景点位集构造函数
   */
  constructor () {
    /**
     * @type {View[]} 初始化列表 && 播放列表
     */
    this.viewList = []
    /**
     * @type {number} 当前播放列表的下标
     */
    this.currentPlay = 0
    /**
     * @type {boolean} 播放状态，false表示未播放，true表示正在播放
     */
    this.playing = false
    /**
     * @type {number} 场景间切换方式，0：全景行走   1：3D行走   2：淡入淡出
     */
    this.switchType = 0
    /**
     * @type {boolean} 自动播放开关，默认自动播放关闭 false：暂停  true：播放
     */
    this.isAutoPlay = false
    /**
     * @type {boolean} 快速导航开关，开启则到达目标点后不再自动旋转
     */
    this.fastTravel = false
    /**
     * @type {number} 自动播放动画所需时长，速度控制
     */
    this.goTime = 2000

    /**
     * @type {Listener} 场景激活框监听器
     */
    this.toSceneViewListener = new Listener()
    /**
     * @type {Listener} 暂停监听器
     */
    this.pauseListener = new Listener()
    /**
     * @type {Listener} 启动自动播放时执行的样式变更方法
     */
    this.startAutoPlayListener = new Listener()

    /**
     * @type {string} 移动被目标id打断的
     */
    this.interruptById = null

    /**
     * @type {string} 移动被
     */
    this.stopFlag = false
  }

  init (renderer, controller) {
    /**
     * @type {Renderer} 渲染器
     */
    this._renderer = renderer
    /**
     * @type {CameraController} 控制器
     */
    this._controller = controller
  }

  set isInterrupt (value) {
    this._controller.interrupt = value
  }

  get isInterrupt () {
    return this._controller.interrupt
  }

  /**
   * 初始化，传入ViewList对象，包含所有场景点位信息
   * @param {object} settings 需求点位id，相机index，经，纬度
   * @param {object[]} modelViews 需求点位id，相机index，经，纬度
   */
  initSceneView (settings, modelViews) {
    this.switchType = settings.switchType
    this.isAutoPlay = settings.isAutoPlay
    this.fastTravel = settings.isQuickNavigation

    this.viewList.length = modelViews.length
    modelViews.sort((a, b) => a.viewOrderId - b.viewOrderId)
    let idx = 0
    for (const v of modelViews) {
      const vAngle = Utils.strToNumberArray(v.viewAngle)
      this.viewList[idx++] = new View(
        v.viewId,
        v.pointId,
        vAngle[0],
        vAngle[1]
      )
    }
    // // test
    // console.log(this.viewList[3].isSingleView)
    // this.viewList[3].pointIndex += 1000
    // console.log(this.viewList[3].isSingleView)
    // this.switchType = 2
    // // test
    this.switchTypeSaved = this.switchType // 暂存switchType，因为单场景点必定淡入淡出，要记录应该是哪种方式切换
  }

  /**
   * 添加一个场景点位
   * @param {Number} id 场景点位id
   * @param {Number} pointIndex 点位对应的相机点位id
   * @param {Number} lon 经度
   * @param {Number} lat 纬度
   */
  addSceneView (id, pointIndex, lon, lat) {
    this.viewList.push(new View(id, pointIndex, lon, lat))
    console.log('add scene success')
  }

  /**
   * 删除点位
   * @param {Number} id 将要删除的id
   * @returns {Boolean} 是否删除成功，是则true
   */
  deleteSceneView (id) {
    for (let i = 0; i < this.viewList.length; ++i) {
      if (this.viewList[i].id === id) {
        // 执行删除
        this.viewList.splice(i, 1)
        return true
      }
    }
    console.log('id not found, do nothing')
    return false
  }

  /**
   * 播放动画到目标场景
   * @param {string} id 目标场景的id
   */
  async toView (id) {
    this._renderer.active = true
    const targetIdx = this.findViweIndex(id)

    this.currentPlay = (targetIdx + 1) % this.viewList.length // 更新下标i
    this.toSceneViewListener.invoke(this.getBannerIndex()) // 执行前端调用的css样式变更方法
    // 执行跳转
    const idx = this.viewList[targetIdx].pointIndex // 目标点位
    const lon = this.viewList[targetIdx].lon
    const lat = this.viewList[targetIdx].lat
    const isSingle = this.viewList[targetIdx].isSingleView
    this._controller.breakDamping()
    if (this.checkCameraMode()) {
      // 全景模式
      await this.toViewOnPano(idx, lon, lat, isSingle)
    } else {
      // 上帝视角模式模式
      await this.toViewOnGod(idx, lon, lat, isSingle)
    }
  }

  /**
   * 从上帝视角移动到目标视角
   * @param {nubmer} idx 目标位置id
   * @param {number} lon 目标经度
   * @param {number} lat 目标纬度
   * @param {boolean} isSingle 是否为单场景点
   */
  async toViewOnGod (idx, lon, lat, isSingle) {
    // 是常规点集点则照常切换
    await this._controller.toPano(idx, lon, lat) // lon lat 仅在切换到单场景时用到
    if (!isSingle) {
      await this._controller.turn2View(lon, lat)
    }
    this.switchType = this.switchTypeSaved // 正常播放完了就更新回暂存的settings里的switchType
  }

  /**
   * 在全景视角移动到目标视角
   * @param {nubmer} idx 目标位置id, singleView的时候这玩意是1000+的一个id
   * @param {number} lon 目标经度
   * @param {number} lat 目标纬度
   * @param {boolean} isSingle 是否为单场景点
   */
  async toViewOnPano (idx, lon, lat, isSingle) {
    const gt = this.isAutoPlay ? this.goTime : config.PanoMode.TransitionTime

    if (isSingle) {
      // 让下一次的切换变成淡入淡出，临时修改this.switchType
      this.switchType = SwitchTypeEnum.Fade
    }
    switch (this.switchType) {
      case SwitchTypeEnum.Pano:
        // 全景游览方式
        await this._controller.toView(this._controller.camera.cameraCurrentIndex, idx, lon, lat, this.fastTravel && this.isAutoPlay, gt, false)
        break
      case SwitchTypeEnum.In3D:
        // 3D游览方式 (目前是与case 0相同)
        // 目前仍然是调用toView方法
        await this._controller.toView(this._controller.camera.cameraCurrentIndex, idx, lon, lat, this.fastTravel && this.isAutoPlay, gt, this.isAutoPlay)
        break
      case SwitchTypeEnum.Fade:
        // 淡入淡出方式
        await this._controller.fadeIn(idx, lon, lat) // 如果是singlePano 则idx>1000，函数里判断
    }
    this.switchType = this.switchTypeSaved // 正常播放完了就更新回settings里的switchType
  }

  /**
   * 快速转向动画，用于在toView过程中再调用ToView的情况
   */
  async quickToView () {
    const idx = this.findViweIndex(this.interruptById)
    this.currentPlay = (idx + 1) % this.viewList.length
    this.toSceneViewListener.invoke(this.getBannerIndex()) // 执行前端调用的css样式变更方法

    await this._controller.fadeIn(this.viewList[idx].pointIndex,
      this.viewList[idx].lon, this.viewList[idx].lat)

    this.interruptById = null
  }

  /**
   * 用户点击下方场景点，实现场景点位间漫游
   * @param {number} id 目标场景点位的id，根据此获取场景点位的具体信息
   */
  async toSceneView (id) {
    if (this.playing) { // 正在播放，打断动画
      this.interruptById = id
      this.isInterrupt = true
    } else {
      this.beforeStartPlay()
      await this.autoPlayCallback(id)
    }
  }

  /**
   * 停止自动播放
   */
  stopAutoPlay () {
    if (this.playing) {
      this.stopFlag = true
    }
  }

  /**
   * 开启自动播放
   */
  async startAutoPlay () {
    if (this._controller.functionLock) return
    this.startAutoPlayListener.invoke() // 启动自动播放时，向下消失的css动画
    this.isAutoPlay = true

    if (!this.playing) {
      this.beforeStartPlay()
      console.log('auto play start')
      await this.autoPlayCallback()
    }
  }

  /**
   * 自动播放回调函数，判断是否继续自动播放或停止
   * @param {boolean} canAutoPlay 控制是否继续自动播放
   * @returns {number} 当前播放到的场景列表的下标
   */
  async autoPlayCallback (id) {
    const targetID = id || this.viewList[this.currentPlay].id

    try {
      console.log('当前正在前往第 ' + targetID + ' 个场景')
      await this.toView(targetID)
    } catch (e) {
      console.error(e)
    }

    // 动画结束后，两种可能，要么被中断结束，要么正常结束。
    if (this.interruptById) { // 被toView打断
      await this.quickToView()
      this.afterStopPlay()
    } else if (this.isAutoPlay) {
      if (this.stopFlag || this.isInterrupt) { // 被点击到了停止或者屏幕事件打断
        this.afterStopPlay()
      } else {
        if (this.switchType === SwitchTypeEnum.Fade) { // 淡入淡出，需要延迟2秒
          await Utils.waitFor(this.goTime)
        }
        this.autoPlayCallback()
      }
    } else {
      this.afterStopPlay()
    }
  }

  /**
   * 在播放之前调用
   */
  beforeStartPlay () {
    this.isInterrupt = false
    this.playing = true
  }

  /**
   * 在停止播放后调用
   */
  afterStopPlay () {
    this.playing = false
    this.stopFlag = false
    this.isAutoPlay = false
    this.isInterrupt = false
    this.pauseListener.invoke() // 执行监听器中的方法
  }

  /**
   * 寻找场景id对应的场景下标
   * @param {string} id 需要寻找的场景的id
   * @returns {nunmber} 场景id对应的下标
   */
  findViweIndex (id) {
    for (let i = 0; i < this.viewList.length; ++i) {
      if (this.viewList[i].id === id) {
        return i
      }
    }
    return undefined
  }

  /**
   * 前端获取相机模式
   * @returns {Boolean} 返回相机当前的模式，true全景、false模型
   */
  checkCameraMode () {
    return this._controller.camera.cameraMode
  }

  /**
   * 自动播放速度控制
   * @param {number} value 接受前端传入的动画时间，用以控制自动播放速度
   */
  setTransitionTime (value) {
    this.goTime = value
  }

  /**
   * 前端监听，返回列表需要active显示的下标
   * @returns {number} 当前播放的下标
   */
  getBannerIndex () {
    return this.currentPlay - 1 === -1 ? this.viewList.length - 1 : this.currentPlay - 1
  }

  /**
   * 提供前端的监听方法，在自动播放被打断时会调用
   * @returns {boolean} 返回自动播放状态，true表示正在播放，false表示停止
   */
  breakAutoPlay () {
    return this.playStatus
  }
}

export {
  ViewSet
}
